import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, Method } from 'axios'
import { Platform } from 'react-native'
// import { RootStore, RootStore } from 'src/data/store'
import { JFSRequest } from '.'
import config from './config'
import {
  getErrorResponseData,
  getHTTPHeaders,
  getHTTPStatus,
  getHTTPStatusCode,
  getServerErrorCode,
  getServerErrorMessage,
  networkConnectionError,
} from './error'
import { Request } from './request'
import {
  Response,
  getResponseData,
  getResponseHTTPHeaders,
  getResponseHTTPStatus,
  getResponseHTTPStatusCode,
} from './response'

const JSONBigIntNative = require('json-bigint')({ useNativeBigInt: true, storeAsString: true })

export const isOnline = () => {
  return true
}

const isIE = () => {
  if (Platform.OS === 'android' || Platform.OS === 'ios') {
    return false
  }
  return /*@cc_on!@*/ false || !!document?.documentMode
}

/* const cache = setupCache({
  maxAge: 15 * 60 * 1000,
}) */

let axios: AxiosInstance = Axios.create()
let refreshTokenRequest: Request<any> | RequestType
let tokenRequestTransformer: (response: Response, originalRequest: any) => void
let cachedTokenResponse: Response
let appRefreshTokenRequest: Request<any> | RequestType
let lastTokenGeneratedTime
let lastAppTokenGeneratedTime
let appTokenRequestTransformer: (response: Response, originalRequest: any) => void
let appCachedTokenResponse: Response
let isSessionRefreshInProgress = false
let isAppSessionRefreshInProgress = false
const sessionExpireResolveList: any[] = []
const sessionExpireRejectList: any[] = []
const appSessionExpireResolveList: any[] = []
const appSessionExpireRejectList: any[] = []

const createSessionExpirePromise = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    sessionExpireResolveList.push(resolve)
    sessionExpireRejectList.push(reject)
  })
}

const createAppSessionExpirePromise = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    appSessionExpireResolveList.push(resolve)
    appSessionExpireRejectList.push(reject)
  })
}

const sessionExpireInterceptor = async (error: any) => {
  const originalRequest = error.config
  if (error?.data?.error?.code === '33607' && !originalRequest._retry && refreshTokenRequest) {
    originalRequest._retry = true
    if (!isSessionRefreshInProgress) {
      if (new Date().getTime() - lastTokenGeneratedTime < 5000) {
        if (tokenRequestTransformer) {
          await tokenRequestTransformer(cachedTokenResponse, originalRequest)
        }
        return executeDirectRequest(originalRequest)
      }
      isSessionRefreshInProgress = true
      let actualRefreshTokenRequest =
        typeof refreshTokenRequest === 'function' ? refreshTokenRequest() : refreshTokenRequest
      return executeDirectRequest(getAxiosRequest(actualRefreshTokenRequest))
        .catch(async (sessionError) => {
          if (tokenRequestTransformer) {
            await tokenRequestTransformer(sessionError, originalRequest)
          }
          sessionExpireRejectList.forEach((reject) => {
            reject(sessionError)
          })
          sessionExpireResolveList.length = 0
          sessionExpireRejectList.length = 0
          isSessionRefreshInProgress = false
          return Promise.reject(sessionError)
        })
        .then(async (resp) => {
          if (tokenRequestTransformer) {
            await tokenRequestTransformer(resp, originalRequest)
          }
          lastTokenGeneratedTime = new Date().getTime()
          if (!resp?.data?.session?.identifierToken && !resp?.data?.session?.userIdentifierToken) {
            sessionExpireRejectList.forEach((reject) => {
              reject(resp)
            })
          } else {
            cachedTokenResponse = { ...resp }
            sessionExpireResolveList.forEach((resolve) => {
              resolve(resp)
            })
          }
          isSessionRefreshInProgress = false
          sessionExpireResolveList.length = 0
          sessionExpireRejectList.length = 0
          return executeDirectRequest(originalRequest)
        })
    } else {
      return createSessionExpirePromise().then(async (resp: Response) => {
        if (tokenRequestTransformer) {
          await tokenRequestTransformer(resp, originalRequest)
        }
        return executeDirectRequest(originalRequest)
      })
    }
  }
  return Promise.reject(error)
}

const generateRandomString = (length: number) => {
  let chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let result = ''
  for (let i = length; i > 0; --i) {
    result += chars[Math.floor(Math.random() * chars.length)]
  }
  return result
}
const appSessionExpireInterceptor = async (error: any) => {
  const originalRequest = error.config
  const isAppAuthenticate = originalRequest?.url?.includes('app/authenticate')
  if (
    error?.data?.error?.code === '33306' &&
    !originalRequest._retry &&
    appRefreshTokenRequest &&
    refreshTokenRequest &&
    !isAppAuthenticate
  ) {
    originalRequest._retry = true
    const respArr: any[] = []

    if (!isAppSessionRefreshInProgress) {
      if (new Date().getTime() - lastAppTokenGeneratedTime < 5000) {
        if (appTokenRequestTransformer) {
          await appTokenRequestTransformer(appCachedTokenResponse, originalRequest)
        }
        if (tokenRequestTransformer) {
          await tokenRequestTransformer(cachedTokenResponse, originalRequest)
        }
        return executeDirectRequest(originalRequest)
      }
      isAppSessionRefreshInProgress = true
      let actualAppRefreshTokenRequest =
        typeof appRefreshTokenRequest === 'function' ? appRefreshTokenRequest() : appRefreshTokenRequest
      let actualRefreshTokenRequest =
        typeof refreshTokenRequest === 'function' ? refreshTokenRequest() : refreshTokenRequest
      return executeDirectRequest(getAxiosRequest(actualAppRefreshTokenRequest))
        .catch(async (sessionError) => {
          if (appTokenRequestTransformer) {
            await appTokenRequestTransformer(sessionError, originalRequest)
          }
          appSessionExpireRejectList.forEach((reject) => {
            reject(sessionError)
          })
          isAppSessionRefreshInProgress = false
          appSessionExpireResolveList.length = 0
          appSessionExpireRejectList.length = 0
          return Promise.reject(sessionError)
        })
        .then(async (resp) => {
          if (appTokenRequestTransformer) {
            await appTokenRequestTransformer(resp, originalRequest)
          }
          lastAppTokenGeneratedTime = new Date().getTime()
          appCachedTokenResponse = { ...resp }
          respArr.push({ ...resp })
          // Required updating with latest tokens else user auth api will throw U50013
          actualRefreshTokenRequest.headers = {
            ...actualRefreshTokenRequest.headers,
            'x-app-access-token': appCachedTokenResponse?.data?.session?.accessToken?.tokenValue ?? '',
            'x-trace-id': generateRandomString(16),
            'x-appid-token': appCachedTokenResponse?.data?.session?.appIdentifierToken ?? '',
          }
          return executeDirectRequest(getAxiosRequest(actualRefreshTokenRequest)).then(async (refResp) => {
            if (tokenRequestTransformer) {
              await tokenRequestTransformer(refResp, originalRequest)
            }
            lastTokenGeneratedTime = new Date().getTime()
            respArr.push({ ...refResp })
            appSessionExpireResolveList.forEach((resolve) => {
              resolve(respArr)
            })
            isAppSessionRefreshInProgress = false
            appSessionExpireResolveList.length = 0
            appSessionExpireRejectList.length = 0
            cachedTokenResponse = { ...refResp }
            return executeDirectRequest(originalRequest)
          })
        })
    } else {
      return createAppSessionExpirePromise()
        .then(async (resp: Response[]) => {
          if (appTokenRequestTransformer) {
            await appTokenRequestTransformer(resp[0], originalRequest)
          }
          if (tokenRequestTransformer) {
            await tokenRequestTransformer(resp[1], originalRequest)
          }
          return executeDirectRequest(originalRequest)
        })
        .catch(async (err) => {
          if (err?.config?.url?.includes('app/authenticate') && appTokenRequestTransformer) {
            await appTokenRequestTransformer(err, originalRequest)
          }
        })
    }
  }
  return Promise.reject(error)
}

axios.interceptors.response.use(
  (response) => {
    if (isIE() && typeof response?.data === 'string') {
      response.data = JSON.parse(response.data)
    }
    const data = response?.data
    if (data?.status === 'FAILURE' && (data?.error?.code === '33607' || data?.error?.code === '33306')) {
      throw response
    }
    return response
  },
  (error) => {
    if (isIE() && typeof error?.response?.data === 'string') {
      error.response.data = JSON.parse(error?.response?.data)
    }
    return Promise.reject(error)
  },
)

axios.interceptors.response.use((response: any) => response, sessionExpireInterceptor)
axios.interceptors.response.use((response: any) => response, appSessionExpireInterceptor)

export const setTokenRefreshParams = (
  request: Request<any>,
  transformer: (response: Response, originalHeaders: any) => void,
) => {
  refreshTokenRequest = request
  tokenRequestTransformer = transformer
}
type RequestType = () => Request<any>

export const setUsrAppRefreshParams = (
  request: Request<any> | RequestType,
  transformer: (response: Response, originalHeaders: any) => void,
  appRefRequest: Request<any> | RequestType,
  appRefTransformer: (response: Response, originalHeaders: any) => void,
) => {
  refreshTokenRequest = request
  tokenRequestTransformer = transformer
  appRefreshTokenRequest = appRefRequest
  appTokenRequestTransformer = appRefTransformer
}

export const executeRequest = async <T>(request: Request<T>) => {
  const serverConfig = request.config || config.default
  const axiosRequest: AxiosRequestConfig = {
    /**
     *  `url` is the server URL that will be used for the request
     */
    url: request.apiPath,

    /**
     *  `method` is the request method to be used when making the request
     */

    method: (request.method as Method) || 'POST',

    paramsSerializer: {
      indexes: null, // no brackets at all
    },

    /**
     * `baseURL` will be prepended to `url` unless `url` is absolute.
     * It can be convenient to set `baseURL` for an instance of axios to pass relative URLs
     * to methods of that instance.
     */
    baseURL: serverConfig.baseUrl,

    /**
     * `transformRequest` allows changes to the request data before it is sent to the server
     * This is only applicable for request methods 'PUT', 'POST', 'PATCH' and 'DELETE'
     * The last function in the array must return a string or an instance of Buffer, ArrayBuffer,
     * FormData or Stream
     * You may modify the headers object.
     */

    // transformRequest: [
    //   (data: string, headers: { [key: string]: string }) => {
    //     return data
    //   },
    // ],

    /**
     * `transformResponse` allows changes to the response data to be made before
     * it is passed to then/catch
     */
    transformResponse: [
      (data: any) => {
        if (typeof data === 'string' && (!request.responseType || request.responseType === 'json')) {
          try {
            data = JSON.parse(data)
          } catch (e) {}
        } else if (typeof data === 'string' && request.responseType === 'text') {
          try {
            data = JSONBigIntNative.parse(data)
          } catch (e) {
            console.log('error', e)
          }
        } else if (request.responseType === 'arraybuffer') {
          try {
            if (data?.byteLength <= 200) {
              const converArrBufferToString = JSON.parse(new TextDecoder('utf-8').decode(data))
              return converArrBufferToString
            }
          } catch (e) {}
        }
        return data
      },
    ],

    /**
     * `headers` are custom headers to be sent
     */

    headers: { ...serverConfig.headers, ...request.headers },

    /**
     * `params` are the URL parameters to be sent with the request
     * Must be a plain object or a URLSearchParams object
     */
    params: request.method === 'GET' || request.method === 'get' ? request.body : undefined,

    /** `data` is the data to be sent as the request body
     * Only applicable for request methods 'PUT', 'POST', and 'PATCH'
     * When no `transformRequest` is set, must be of one of the following types:
     * - string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams
     * - Browser only: FormData, File, Blob
     * - Node only: Stream, Buffer
     */

    data: request.method !== 'GET' && request.method !== 'get' ? request.body : undefined,
    signal: request?.config?.abortController?.signal,
    /**
     * `timeout` specifies the number of milliseconds before the request times out.
     * If the request takes longer than `timeout`, the request will be aborted.
     */

    timeout: 0, // default is `0` (no timeout)

    // `withCredentials` indicates whether or not cross-site Access-Control requests
    // should be made using credentials
    withCredentials: false, // default

    // `responseType` indicates the type of data that the server will respond with
    // options are: 'arraybuffer', 'document', 'json', 'text', 'stream'
    //   browser only: 'blob'
    responseType:
      request.responseType === 'text' || request.responseType === 'arraybuffer' ? request.responseType : 'json', // default json text undefined to make resonse as string

    // `xsrfCookieName` is the name of the cookie to use as a value for xsrf token
    xsrfCookieName: 'XSRF-TOKEN', // default

    // `xsrfHeaderName` is the name of the http header that carries the xsrf token value
    xsrfHeaderName: 'X-XSRF-TOKEN', // default

    // `onUploadProgress` allows handling of progress events for uploads
    // onUploadProgress(progressEvent) {
    //   // Do whatever you want with the native progress event
    // },

    // `onDownloadProgress` allows handling of progress events for downloads
    // onDownloadProgress(progressEvent) {
    //   // Do whatever you want with the native progress event
    // },

    // `maxContentLength` defines the max size of the http response content in bytes allowed
    //maxContentLength: 2000,

    // `validateStatus` defines whether to resolve or reject the promise for a given
    // HTTP response status code. If `validateStatus` returns `true` (or is set to `null`
    // or `undefined`), the promise will be resolved; otherwise, the promise will be
    // rejected.
    validateStatus(status: number) {
      return status >= 200 && status < 300 // default
    },

    // `maxRedirects` defines the maximum number of redirects to follow in node.js.
    // If set to 0, no redirects will be followed.
    maxRedirects: 5, // default
  }

  if (isOnline()) {
    console.log('Network : ', cachedTokenResponse)
    // tslint:disable-next-line
    console.log('Axios Request::', axiosRequest)
    return axios
      .request(axiosRequest)
      .then((response: any) => {
        // tslint:disable-next-line
        console.log('Axios Response::', response)
        return parseAxiosResponse(response)
      })
      .catch((error: any) => {
        if (!error) {
          console.log('Error is undefined for ', request)
          throw { message: 'Something went wrong' }
        }
        // tslint:disable-next-line
        console.log('Axios Error::', error)
        throw parseAxiosError(error)
      })
  }
  return Promise.reject(parseAxiosError(networkConnectionError()))
}

const executeDirectRequest = async (request: any) => {
  if (isOnline()) {
    // tslint:disable-next-line
    console.log('Axios Request::', request)
    // tslint:disable-next-line
    return Axios.create()
      .request(request)
      .then((response: any) => {
        // tslint:disable-next-line
        console.log('Axios Response::', response)
        return parseAxiosResponse(response)
      })
      .catch((error: any) => {
        return Promise.reject(error)
      })
  }
  return Promise.reject(parseAxiosError(networkConnectionError()))
}

const parseAxiosResponse = (response: AxiosResponse) => {
  const newResponse: Response = {
    data: getResponseData(response),
    httpCode: getResponseHTTPStatusCode(response),
    errorCode: 0,
    status: getResponseHTTPStatus(response),
    message: undefined, // to be derived form error.response.data
    headers: getResponseHTTPHeaders(response),
  }
  return newResponse
}

const parseAxiosError = (error: AxiosError) => {
  const response: Response = {
    data: getErrorResponseData(error),
    httpCode: getHTTPStatusCode(error),
    errorCode: getServerErrorCode(error),
    status: getHTTPStatus(error),
    message: getServerErrorMessage(error),
    headers: getHTTPHeaders(error),
  }
  // tslint:disable-next-line
  console.log('Axios Error:', response)
  return response
}

const getAxiosRequest = <T>(request: Request<T>) => {
  const serverConfig = request.config || config.default
  const axiosRequest: AxiosRequestConfig = {
    /**
     *  `url` is the server URL that will be used for the request
     */
    url: request.apiPath,

    /**
     *  `method` is the request method to be used when making the request
     */

    method: (request.method as Method) || 'POST',

    /**
     * `baseURL` will be prepended to `url` unless `url` is absolute.
     * It can be convenient to set `baseURL` for an instance of axios to pass relative URLs
     * to methods of that instance.
     */
    baseURL: serverConfig.baseUrl,

    /**
     * `transformRequest` allows changes to the request data before it is sent to the server
     * This is only applicable for request methods 'PUT', 'POST', 'PATCH' and 'DELETE'
     * The last function in the array must return a string or an instance of Buffer, ArrayBuffer,
     * FormData or Stream
     * You may modify the headers object.
     */

    // transformRequest: [
    //   (data: string, headers: { [key: string]: string }): any => {
    //     return data
    //   },
    // ],

    /**
     * `transformResponse` allows changes to the response data to be made before
     * it is passed to then/catch
     */
    // transformResponse: [
    //   (data: string) => {
    //     return data
    //   },
    // ],

    /**
     * `headers` are custom headers to be sent
     */

    headers: { ...serverConfig.headers, ...request.headers },

    /**
     * `params` are the URL parameters to be sent with the request
     * Must be a plain object or a URLSearchParams object
     */
    params: request.method === 'GET' || request.method === 'get' ? request.body : undefined,

    /** `data` is the data to be sent as the request body
     * Only applicable for request methods 'PUT', 'POST', and 'PATCH'
     * When no `transformRequest` is set, must be of one of the following types:
     * - string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams
     * - Browser only: FormData, File, Blob
     * - Node only: Stream, Buffer
     */

    data: request.method !== 'GET' && request.method !== 'get' ? request.body : undefined,

    /**
     * `timeout` specifies the number of milliseconds before the request times out.
     * If the request takes longer than `timeout`, the request will be aborted.
     */

    timeout: 0, // default is `0` (no timeout)

    // `withCredentials` indicates whether or not cross-site Access-Control requests
    // should be made using credentials
    withCredentials: false, // default

    // `responseType` indicates the type of data that the server will respond with
    // options are: 'arraybuffer', 'document', 'json', 'text', 'stream'
    //   browser only: 'blob'
    responseType: request.config?.responseType || 'json', // default

    // `xsrfCookieName` is the name of the cookie to use as a value for xsrf token
    xsrfCookieName: 'XSRF-TOKEN', // default

    // `xsrfHeaderName` is the name of the http header that carries the xsrf token value
    xsrfHeaderName: 'X-XSRF-TOKEN', // default

    // `onUploadProgress` allows handling of progress events for uploads
    // onUploadProgress(progressEvent) {
    //   // Do whatever you want with the native progress event
    // },

    // `onDownloadProgress` allows handling of progress events for downloads
    // onDownloadProgress(progressEvent) {
    //   // Do whatever you want with the native progress event
    // },

    // `maxContentLength` defines the max size of the http response content in bytes allowed
    maxContentLength: 2000,

    // `validateStatus` defines whether to resolve or reject the promise for a given
    // HTTP response status code. If `validateStatus` returns `true` (or is set to `null`
    // or `undefined`), the promise will be resolved; otherwise, the promise will be
    // rejected.
    validateStatus(status) {
      return status >= 200 && status < 300 // default
    },

    // `maxRedirects` defines the maximum number of redirects to follow in node.js.
    // If set to 0, no redirects will be followed.
    maxRedirects: 5, // default
  }
  return axiosRequest
}

const updateSsoToken = (_: string) => {
  // RootStore.user.session.updateToken(ssoToken)
}

let RootStore: any
const reFreshTokenrequest = <T>(request: Request<T>) => {
  const tokenRequest: JFSRequest<{}> = {
    apiPath: 'user/auth/regenerate',
    method: 'POST',
    config: config.default,
    headers: {
      Authorization: RootStore?.user?.session?.ssoToken ?? '',
    },
    body: {},
  }

  const refreshTokenrequest = getAxiosRequest(tokenRequest)
  console.log('Axios token Request::', refreshTokenrequest)

  const axiosTokenRequest = Axios.request(refreshTokenrequest)
    .then((response) => {
      console.log('Axios token response', response)

      const tokenResponse = parseAxiosResponse(response)

      const { token } = tokenResponse?.data?.data
      updateSsoToken(token)

      const newRequest: JFSRequest<{}> = {
        ...request,
        headers: { Authorization: token },
      }

      const newAxiosRequest = getAxiosRequest(newRequest)
      console.log('Axios re-Request with updated token::', newAxiosRequest)
      // request previous failed api with updated refresh token
      return Axios.request(newAxiosRequest)
    })
    .catch((tokenerror) => {
      const newError = {
        ...tokenerror,
        response: {
          ...tokenerror.response,
          data: {
            ...tokenerror.response.data,
            errorCode: +'-10002', // any error while refresh token - logout session
          },
        },
      }
      throw newError
    })

  return axiosTokenRequest
}

export const executeRequestNew = <T>(request: Request<T>) => {
  console.log(request.body)
  // const serverConfig = request.config || config.default
  const axiosRequest: AxiosRequestConfig = getAxiosRequest(request)

  if (isOnline()) {
    // tslint:disable-next-line
    console.log('Axios Request::', axiosRequest)
    return Axios.request(axiosRequest)
      .then((response) => {
        return response
      })
      .catch((error) => {
        // tslint:disable-next-line
        console.log('Axios Error::', JSON.stringify(error))

        // const errorCode = getServerErrorCode(error)
        const data = getErrorResponseData(error)

        const errorCategory = data?.errorKey?.errorCategory
        if (errorCategory === 'INVALID TOKEN') {
          return reFreshTokenrequest(request)
        }
        throw error
      })
      .then((response) => {
        // tslint:disable-next-line
        console.log('Axios Response::', response)
        if (response.headers['Content-type'] === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          return response
        }
        return parseAxiosResponse(response)
      })
      .catch((error) => {
        const parsedError = parseAxiosError(error)
        console.log('Axios Error::', parsedError)
        throw parsedError
      })
  }
  return Promise.reject(parseAxiosError(networkConnectionError()))
}
